import Backbone from 'backbone';

import {LaunchDarklyFlag, LaunchDarklyFlags} from 'launchdarkly/backbone/LaunchDarklyFlagsCollection';

/**
 * A Backbone view that evaluates flags and rerenders after LaunchDarkly initialization. Extend this view if
 * you are evaluating LD flags in Backbone view logic.
 */
export abstract class LaunchDarklyListeningView<TModel extends Backbone.Model> extends Backbone.View<TModel> {
  flags!: LaunchDarklyFlags;

  override initialize(options?: Backbone.ViewOptions<TModel>): void {
    // Get a handle on the global flags `SingletonCollection`...
    this.flags = LaunchDarklyFlags.get();
    // ...and listen for when it finishes grabbing the flags. Rerender if any known flag is one this view
    // cares about.
    this.listenTo(this.flags, 'change add remove', (model: LaunchDarklyFlag) => {
      if (this.shouldRerenderOnLDKeyChange(model.id)) {
        this.rerenderOnLDFlagChange();
      }
    });
  }

  /**
   * @returns A map of LD keys this view cares about to what the default value of that key should be, before
   *          the LD client has initialized and flags have been fetched.
   */
  abstract getLDFlagKeysAndDefaults(): Record<string, boolean>;

  /**
   * Tries to get the value of the LD flag, with fallbacks in the following order:
   * * value from `LaunchDarklyFlags` `SingletonCollection`
   * * default value as defined by implementing class method override `getLDFlagKeysAndDefaults`
   * * false (i.e. flag off)
   */
  getLDFlagValueWithDefault(key: string): boolean {
    return this.flags?.get(key)?.get('flagValue') ?? this.getLDFlagKeysAndDefaults()[key] ?? false;
  }

  /**
   * Dependency free function to rerender this view when flags update.
   */
  abstract rerenderOnLDFlagChange(): void;

  /**
   * Whether a change to a flag with this key should cause this view to rerender
   */
  shouldRerenderOnLDKeyChange(key: string): boolean {
    const keysAndDefaults = this.getLDFlagKeysAndDefaults();
    return keysAndDefaults.hasOwnProperty(key);
  }
}
